/**
 * InspectorPanel Component - Sidebar Inspector
 *
 * Shows detailed information about the currently inspected node:
 * - Value with full precision
 * - Type badge (Source / Assumption / Computed)
 * - For sources: Citation with link
 * - For assumptions: Slider, presets, range info
 * - For computed: Formula with clickable inputs
 * - "What uses this?" list of dependent nodes
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useGraph } from '../GraphContext';
import { useInspectedNode, useDependents, usePopup } from '../hooks';
import { isSourceNode, isAssumptionNode, isComputedNode, AssumptionNode, NodeId, Node, ComputedNode, SeriesMeta } from '../../types';
import { FormulaDisplay } from './FormulaDisplay';
import { InlineAdjust } from './InlineAdjust';
import { Popup } from './Popup';
import { TemporalSeriesView } from './TemporalSeriesView';
import { SeriesPreview } from './SeriesPreview';

export interface InspectorPanelProps {
  // Position: fixed right sidebar or inline
  position?: 'sidebar' | 'inline';
  // Width for sidebar mode
  width?: number;
}

export function InspectorPanel({
  position = 'sidebar',
  width = 520,
}: InspectorPanelProps): React.ReactElement | null {
  const ctx = useGraph();
  const { nodeId, node, value, setInspectedNodeId } = useInspectedNode();
  const dependents = useDependents(nodeId || '');

  // Track navigation history for back button
  // Only builds history for clicks WITHIN the sidebar, not external report clicks
  const [history, setHistory] = useState<NodeId[]>([]);
  const internalNavTarget = React.useRef<NodeId | null>(null);

  // Clear history when navigating from outside the sidebar (clicking in report)
  useEffect(() => {
    if (nodeId) {
      // If this nodeId doesn't match our internal navigation target, it was external
      if (nodeId !== internalNavTarget.current) {
        setHistory([]);
      }
      internalNavTarget.current = null; // Reset for next navigation
    }
  }, [nodeId]);

  // Don't render if nothing is inspected
  if (!nodeId || !node || value === undefined) {
    return null;
  }

  const handleClose = () => {
    setHistory([]);
    setInspectedNodeId(null);
  };

  const handleNavigate = (targetId: NodeId) => {
    // Push current node to history (internal navigation)
    if (nodeId) {
      setHistory(prev => [...prev, nodeId]);
    }
    internalNavTarget.current = targetId;
    setInspectedNodeId(targetId);
  };

  const handleBack = () => {
    if (history.length > 0) {
      const newHistory = [...history];
      const previousId = newHistory.pop();
      setHistory(newHistory);
      if (previousId) {
        internalNavTarget.current = previousId;
        setInspectedNodeId(previousId);
      }
    }
  };

  // Get direct dependencies for computed nodes
  const deps = isComputedNode(node) ? node.deps : [];

  // Container styles based on position
  // top-12 accounts for the sticky header (h-12)
  const containerClass = position === 'sidebar'
    ? 'fixed right-0 top-12 h-[calc(100vh-3rem)] bg-white border-l border-gray-200 overflow-y-auto z-40'
    : 'border border-gray-200 bg-white';

  return (
    <div
      className={containerClass}
      style={position === 'sidebar' ? { width } : undefined}
    >
      {/* Header */}
      <div className="sticky top-0 bg-white border-b border-gray-200 p-4 z-10">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-2">
            {/* Back button */}
            {history.length > 0 && (
              <button
                onClick={handleBack}
                className="text-gray-400 hover:text-gray-600 p-1 -ml-1"
                aria-label="Go back"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div>
              <h3 className="font-semibold text-gray-900">{node.label}</h3>
              {node.description && (
                <p className="text-sm text-gray-500 mt-1">{node.description}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Close inspector"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Type badge */}
        <div className="mt-2">
          {isSourceNode(node) && (
            <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600">Source</span>
          )}
          {isAssumptionNode(node) && (
            <span className="text-xs px-2 py-1 bg-gray-200 text-gray-700">Assumption</span>
          )}
          {isComputedNode(node) && (
            <span className="text-xs px-2 py-1 bg-gray-300 text-gray-800">Computed</span>
          )}
        </div>
      </div>

      {/* Value display */}
      <div className="p-4 border-b border-gray-100">
        <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Current Value</div>
        <div className="text-2xl font-bold text-gray-900">
          {ctx.formatValue(value, node.unit)}
        </div>
        {ctx.isOverridden(nodeId) && (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-xs text-gray-500">Adjusted from default</span>
            <button
              onClick={() => ctx.clearOverride(nodeId)}
              className="text-xs text-gray-600 underline hover:text-gray-800"
            >
              Reset
            </button>
          </div>
        )}
      </div>

      {/* Source-specific: Citation */}
      {isSourceNode(node) && (
        <div className="p-4 border-b border-gray-100">
          <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Source</div>
          <div className="text-sm">
            <p className="font-medium text-gray-800">{node.citation.source}</p>
            {node.citation.description && (
              <p className="text-gray-600 mt-1">{node.citation.description}</p>
            )}
            {node.citation.url && (
              <a
                href={node.citation.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 mt-2 text-gray-600 hover:text-gray-800 text-sm"
              >
                View source
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            )}
          </div>
        </div>
      )}

      {/* Assumption-specific: Adjustment controls */}
      {isAssumptionNode(node) && (
        <AssumptionControls nodeId={nodeId} node={node} value={value} />
      )}

      {/* Computed-specific: Formula and inputs */}
      {isComputedNode(node) && (
        <>
          <div className="p-4 border-b border-gray-100">
            <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">Formula</div>
            <div className="bg-gray-50 p-3 border border-gray-100">
              <FormulaDisplay formula={node.formula} maxDepth={3} onNavigate={handleNavigate} />
            </div>

            {/* Simple inputs list - replaces complex derivation tree */}
            {deps.length > 0 && (
              <div className="mt-4">
                <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
                  Inputs ({deps.length})
                </div>
                <div className="space-y-1">
                  {deps.map((depId) => {
                    const depNode = ctx.getNode(depId);
                    const depValue = ctx.getValue(depId);
                    if (!depNode || depValue === undefined) return null;

                    return (
                      <InputItem
                        key={depId}
                        nodeId={depId}
                        node={depNode}
                        value={depValue}
                        onNavigate={handleNavigate}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Temporal series view - show if this node is part of a series */}
          {(node as ComputedNode).seriesMeta && (
            <TemporalSeriesView
              seriesMeta={(node as ComputedNode).seriesMeta!}
              currentYear={(node as ComputedNode).seriesMeta!.year}
              onYearClick={(yearNodeId) => handleNavigate(yearNodeId)}
            />
          )}
        </>
      )}

      {/* Dependents: What uses this? */}
      <DependentsSection dependents={dependents} onNavigate={handleNavigate} />
    </div>
  );
}

/**
 * Dependents section with series grouping
 * Groups nodes that belong to the same series and shows them as a SeriesPreview
 */
function DependentsSection({
  dependents,
  onNavigate,
}: {
  dependents: NodeId[];
  onNavigate: (id: NodeId) => void;
}): React.ReactElement | null {
  const ctx = useGraph();

  // Group dependents by series
  const { seriesGroups, nonSeriesItems } = useMemo(() => {
    const seriesMap = new Map<string, { meta: SeriesMeta; nodeIds: NodeId[] }>();
    const nonSeries: NodeId[] = [];

    for (const depId of dependents) {
      const depNode = ctx.getNode(depId);
      if (depNode && isComputedNode(depNode) && depNode.seriesMeta) {
        const { seriesId } = depNode.seriesMeta;
        if (!seriesMap.has(seriesId)) {
          seriesMap.set(seriesId, { meta: depNode.seriesMeta, nodeIds: [] });
        }
        seriesMap.get(seriesId)!.nodeIds.push(depId);
      } else {
        nonSeries.push(depId);
      }
    }

    return {
      seriesGroups: Array.from(seriesMap.values()),
      nonSeriesItems: nonSeries,
    };
  }, [dependents, ctx]);

  if (dependents.length === 0) {
    return null;
  }

  return (
    <div className="p-4">
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-2">
        Used By ({dependents.length} nodes{seriesGroups.length > 0 ? `, ${seriesGroups.length} series` : ''})
      </div>
      <div className="space-y-2">
        {/* Series groups - show as compact previews */}
        {seriesGroups.map(({ meta }) => (
          <SeriesPreview
            key={meta.seriesId}
            seriesId={meta.seriesId}
            seriesLabel={meta.seriesLabel}
            pattern={meta.pattern}
            totalYears={meta.totalYears}
            onYearClick={onNavigate}
          />
        ))}

        {/* Non-series items - show as individual buttons */}
        {nonSeriesItems.slice(0, 5).map((depId) => {
          const depNode = ctx.getNode(depId);
          return (
            <button
              key={depId}
              onClick={() => onNavigate(depId)}
              className="block w-full text-left text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 px-2 py-1"
            >
              {depNode?.label || depId}
            </button>
          );
        })}
        {nonSeriesItems.length > 5 && (
          <div className="text-xs text-gray-400 px-2">
            +{nonSeriesItems.length - 5} more
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Assumption adjustment controls
 */
function AssumptionControls({
  nodeId,
  node,
  value,
}: {
  nodeId: string;
  node: AssumptionNode;
  value: number;
}): React.ReactElement {
  const ctx = useGraph();
  const [inputValue, setInputValue] = React.useState(String(value));
  const [isEditing, setIsEditing] = React.useState(false);

  // Update input when value changes externally
  React.useEffect(() => {
    if (!isEditing) {
      setInputValue(String(value));
    }
  }, [value, isEditing]);

  const range = node.range || { min: 0, max: value * 2 };
  const step = range.step || (node.unit === 'percent' ? 0.1 :
    node.unit === 'currency' ? Math.max(1000, Math.round(value * 0.01)) : 1);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    ctx.setOverride(nodeId, newValue);
  };

  const handleInputSubmit = () => {
    const parsed = parseFloat(inputValue);
    if (!isNaN(parsed)) {
      ctx.setOverride(nodeId, Math.max(range.min, Math.min(range.max, parsed)));
    }
    setIsEditing(false);
  };

  return (
    <div className="p-4 border-b border-gray-100">
      <div className="text-xs text-gray-500 uppercase tracking-wide mb-3">Adjust Value</div>

      {/* Slider */}
      <div className="mb-4">
        <input
          type="range"
          min={range.min}
          max={range.max}
          step={step}
          value={value}
          onChange={handleSliderChange}
          className="w-full h-2 bg-gray-200 appearance-none cursor-pointer accent-gray-700"
        />
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{ctx.formatValue(range.min, node.unit)}</span>
          <span>{ctx.formatValue(range.max, node.unit)}</span>
        </div>
      </div>

      {/* Direct input */}
      <div className="mb-4">
        <label className="text-xs text-gray-500 block mb-1">Direct Input</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => {
              setInputValue(e.target.value);
              setIsEditing(true);
            }}
            onBlur={handleInputSubmit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleInputSubmit();
            }}
            className="flex-1 border border-gray-200 px-2 py-1 text-sm focus:outline-none focus:border-gray-400"
          />
          <span className="text-sm text-gray-500 py-1">
            {node.unit === 'percent' ? '%' : node.unit === 'currency' ? '$' : ''}
          </span>
        </div>
      </div>

      {/* Presets */}
      {node.presets && Object.keys(node.presets).length > 0 && (
        <div>
          <label className="text-xs text-gray-500 block mb-2">Presets</label>
          <div className="space-y-1">
            {Object.entries(node.presets).map(([key, preset]) => (
              <button
                key={key}
                onClick={() => ctx.setOverride(nodeId, preset.value)}
                className={`w-full text-left px-3 py-2 text-sm border ${
                  value === preset.value
                    ? 'bg-gray-100 border-gray-300'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <div className="font-medium text-gray-800">{preset.label}</div>
                {preset.description && (
                  <div className="text-xs text-gray-500 mt-0.5">{preset.description}</div>
                )}
                {preset.citation && (
                  <div className="text-xs text-gray-400 mt-0.5">
                    Source: {preset.citation.source}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Input item with hover popup for adjustment (assumptions) or formula preview (computed)
 */
function InputItem({
  nodeId,
  node,
  value,
  onNavigate,
}: {
  nodeId: NodeId;
  node: Node;
  value: number;
  onNavigate: (id: NodeId) => void;
}): React.ReactElement {
  const ctx = useGraph();
  const triggerRef = React.useRef<HTMLDivElement>(null);

  // Use centralized popup hook for consistent behavior
  const popup = usePopup();

  const isAssumption = isAssumptionNode(node);
  const isComputed = isComputedNode(node);

  return (
    <div
      ref={triggerRef}
      className="relative"
      {...popup.triggerProps}
    >
      <button
        onClick={() => onNavigate(nodeId)}
        className="w-full text-left px-3 py-2 text-sm border border-gray-100 hover:border-gray-200 hover:bg-gray-50 flex justify-between items-center group"
      >
        <span className="text-gray-700">{node.label}</span>
        <span className="flex items-center gap-2">
          <span className="text-gray-900 font-medium">
            {ctx.formatValue(value, node.unit)}
          </span>
          <svg className="w-4 h-4 text-gray-300 group-hover:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </span>
      </button>

      {/* Assumption: Show inline adjust */}
      {popup.isVisible && isAssumption && (
        <InlineAdjust
          nodeId={nodeId}
          node={node}
          value={value}
          onClose={popup.hide}
          onMouseEnter={popup.onPopupMouseEnter}
          onMouseLeave={popup.onPopupMouseLeave}
          triggerRef={triggerRef}
        />
      )}

      {/* Computed: Show formula preview */}
      {popup.isVisible && isComputed && (
        <Popup isVisible {...popup.popupProps} minWidth="min-w-72" triggerRef={triggerRef}>
          <div className="text-xs text-gray-500 mb-1">Formula</div>
          <FormulaDisplay formula={node.formula} onNavigate={onNavigate} />
        </Popup>
      )}
    </div>
  );
}

export default InspectorPanel;
